/* eslint-disable max-lines-per-function */
import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector, createSelectorFactory, defaultMemoize } from '@ngrx/store';

import { DepartmentSelectors } from '@recruitee/department-data';
import { LocationSelectors } from '@recruitee/locations-data';
import type { ID } from '@recruitee/ngrx';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';
import type { ApprovalRequestData, Requisition } from '@recruitee/requisitions-types';
import { RequisitionsScope } from '@recruitee/requisitions-types';

import type {
  DuplicateViewContext,
  RequisitionApprovalRequestUpdateData,
  RequisitionCreateData,
  RequisitionsNonGroupedParams,
  RequisitionUpdateData,
  UpdateContext,
} from './requisitions.types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<Requisition>()(
  'Requisitions',
  {
    references: makeRef => [
      makeRef('departmentId', 'department', id => DepartmentSelectors.byId(id!)),
      makeRef('locationIds', 'locations', ids => LocationSelectors.byIds(ids)),
    ],
    customActions: adapter => {
      const actions = getEntityCollectionActions(adapter, ({ id }) => id!);
      return {
        fetchCollection: (state, payload?: RequisitionsNonGroupedParams) =>
          actions.fetchCollection(state, payload),
        fetchCollectionSuccess: (
          state,
          payload: { data: Requisition[]; scope: RequisitionsScope },
        ) => actions.fetchCollectionSuccess(state, payload),
        fetchCollectionFailed: (
          state,
          payload: { error: HttpErrorResponse; scope: RequisitionsScope },
        ) => actions.fetchCollectionFailed(state, payload),
        fetchApprovedCollection: state => actions.fetchCollection(state),
        fetchApprovedCollectionSuccess: (
          state,
          payload: { data: Requisition[]; scope: RequisitionsScope },
        ) => actions.fetchCollectionSuccess(state, payload),
        fetchApprovedCollectionFailed: (
          state,
          payload: { error: HttpErrorResponse; scope: RequisitionsScope },
        ) => actions.fetchCollectionFailed(state, payload),
        updateOnePessimistic: (
          state,
          payload: { data: RequisitionUpdateData; context: UpdateContext },
        ) => adapter.markAsPending(state),
        updateOnePessimisticSuccess: (
          state,
          payload: { data: Requisition; context: UpdateContext },
        ) => actions.updateOnePessimisticSuccess(state, payload),
        deleteOpenings: (
          state,
          payload: {
            data: Pick<RequisitionUpdateData, 'openings' | 'id'>;
            openingsDeleted: number;
          },
        ) => adapter.markAsPending(state),
        deleteOpeningsSuccess: (state, payload: { data: Requisition; openingsDeleted: number }) =>
          pipeState(
            state,
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: null }),
            next => adapter.updateOne(next, payload.data),
          ),
        deleteOpeningsFailed: actions.updateOnePessimisticFailed,
        requestApproval: (state, payload: { data: RequisitionApprovalRequestUpdateData }) =>
          adapter.markAsPending(state),
        requestApprovalSuccess: actions.updateOnePessimisticSuccess,
        requestApprovalFailed: actions.updateOnePessimisticFailed,
        cancelApprovalRequest: (state, payload: { id: ID }) => adapter.markAsPending(state),
        cancelApprovalRequestSuccess: actions.updateOnePessimisticSuccess,
        cancelApprovalRequestFailed: actions.updateOnePessimisticFailed,
        setApprovedDialogViewed: (state, payload: { requisitionId: number }) =>
          adapter.markAsPending(state, { entities: [payload.requisitionId], scope: '' }),
        setApprovedDialogViewedSuccess: (
          state,
          payload: { requisitionId: number; approvalRequest: ApprovalRequestData },
        ) =>
          pipeState(
            state,
            next => {
              let entity = { ...next.entityMap[payload.requisitionId]! };
              entity = {
                ...entity,
                approvalRequest: { ...payload.approvalRequest },
              };
              return adapter.updateOne(next, entity);
            },
            next => adapter.markAsLoaded(next, { entities: [payload.requisitionId], scope: '' }),
          ),
        setApprovedDialogViewedFailed: (state, payload: { id: ID; error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error, { entities: [payload.id], scope: '' }),
        createOnePessimistic: (
          state,
          payload: { data: RequisitionCreateData; correlationId: string },
        ) => adapter.markAsPending(state),
        duplicate: (
          state,
          payload: {
            data: { id: number; correlationId: string };
            viewContext: DuplicateViewContext;
          },
        ) => adapter.markAsPending(state),
        duplicateSuccess: (
          state,
          payload: { data: Requisition; correlationId: string; viewContext: DuplicateViewContext },
        ) =>
          pipeState(
            state,
            next =>
              adapter.addOne(next, payload.data, {
                scope: RequisitionsScope.Active,
                prepend: true,
              }),
            next => adapter.markAsLoaded(next),
          ),
        duplicateFailed: actions.createOnePessimisticFailed,
        archive: (state, payload: { id: ID }) => adapter.markAsPending(state),
        archiveSuccess: (state, payload: { data: Requisition }) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.data.id),
            next =>
              adapter.addOne(next, payload.data, {
                scope: RequisitionsScope.Archived,
              }),
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: '' }),
          ),
        archiveFailed: actions.updateOnePessimisticFailed,
        retrieve: (state, payload: { id: ID }) => adapter.markAsPending(state),
        retrieveSuccess: (state, payload: { data: Requisition }) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.data.id),
            next =>
              adapter.addOne(next, payload.data, {
                scope: RequisitionsScope.Active,
              }),
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: '' }),
          ),
        retrieveFailed: actions.updateOnePessimisticFailed,
        updateTitle: (state, payload: { data: { id: number; title: string } }) =>
          adapter.markAsPending(state),
        updateTitleSuccess: (state, payload: { data: Requisition }) =>
          pipeState(
            state,
            next => {
              const entity = { ...next.entityMap[payload.data.id] };
              return adapter.updateOne(next, { ...entity, title: payload.data.title });
            },
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: null }),
          ),
        updateTitleFailed: actions.updateOnePessimisticFailed,
        follow: (state, payload: { id: ID }) => adapter.markAsPending(state),
        followSuccess: actions.updateOnePessimisticSuccess,
        followFailed: actions.updateOnePessimisticFailed,
        unfollow: (state, payload: { id: ID }) => adapter.markAsPending(state),
        unfollowSuccess: actions.updateOnePessimisticSuccess,
        unfollowFailed: actions.updateOnePessimisticFailed,
        unassignCandidate: ngrxEvent<{ openingId: number; requisitionId: number }>(),
        unassignCandidateSuccess: ngrxEvent<{ requisitionId: number }>(),
        unassignCandidateFailed: ngrxEvent<{
          error: HttpErrorResponse;
        }>(),
        setFlow: (state, payload: { requisitionId: number; flow: { id: number; title: string } }) =>
          adapter.markAsPending(state),
        setFlowSuccess: (state, payload: { data: Requisition; flow: { title: string } }) =>
          pipeState(
            state,
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: null }),
            next => adapter.updateOne(next, payload.data),
          ),
        setFlowFailed: actions.updateOnePessimisticFailed,
        flowReassigned: ngrxEvent<{ requisitionId: number; newFlowId: number }>(),
        upsertReferences: (state, payload: { data: Requisition[]; scope: RequisitionsScope }) =>
          adapter.upsertMany(state, payload.data, { scope: payload.scope }),
      };
    },
  },
);

const allActive = memoizeSelector(() =>
  createSelector(
    selectors.entitiesWithReferences({ scope: RequisitionsScope.Active }),
    requisitions => requisitions.sort((a, b) => b.id - a.id),
  ),
);

const byIdsWithReferences = memoizeSelector((ids: number[]) =>
  createSelectorFactory(defaultMemoize)(
    ...(ids || []).map(id => selectors.byIdWithReferences(id)),
    (...result: Requisition[]) => result.filter(req => !!req),
  ),
);

const RequisitionsSelectors = {
  ...selectors,
  allActive,
  byIdsWithReferences,
};

export {
  actions as RequisitionsActions,
  RequisitionsSelectors,
  reducer as RequisitionsReducer,
  namespace as RequisitionsNamespace,
};
